
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";

interface AIMetrics {
  avg_analysis_time: number;
  day: string;
  total_attempts: number;
  avg_confidence: number;
  success_rate: number;
  avg_parse_time: number;
  most_common_meeting_type: string;
  meeting_type_variety: number;
}

export const AIPerformanceSection = () => {
  const [metrics, setMetrics] = useState<AIMetrics[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const { data, error } = await supabase
          .from('ai_performance_metrics')
          .select('*')
          .order('day', { ascending: true })
          .limit(30);

        if (error) throw error;
        
        if (data) {
          const formattedData = data.map(metric => ({
            ...metric,
            success_rate: Number((metric.success_rate * 100).toFixed(1)),
            avg_confidence: Number((metric.avg_confidence * 100).toFixed(1)),
            avg_analysis_time: Number((metric.avg_analysis_time || 0).toFixed(2)),
            avg_parse_time: Number((metric.avg_parse_time || 0).toFixed(2)),
            day: new Date(metric.day).toLocaleDateString()
          }));
          setMetrics(formattedData);
        }
      } catch (error) {
        console.error('Error fetching AI metrics:', error);
        toast({
          title: "Error",
          description: "Failed to fetch AI performance metrics",
          variant: "destructive",
        });
      }
    };

    fetchMetrics();
  }, []);

  const latestMetrics = metrics[metrics.length - 1] || {
    success_rate: 0,
    avg_confidence: 0,
    total_attempts: 0,
    most_common_meeting_type: 'N/A'
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">AI Performance Metrics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-green-600">
              {latestMetrics.success_rate}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Confidence Score</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-blue-600">
              {latestMetrics.avg_confidence}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Daily Attempts</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-purple-600">
              {latestMetrics.total_attempts}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Common Meeting Type</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-medium text-gray-600">
              {latestMetrics.most_common_meeting_type}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Success Rate Trend</CardTitle>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="success_rate" 
                  stroke="#22c55e" 
                  name="Success Rate (%)"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Confidence Score Trend</CardTitle>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="avg_confidence" 
                  stroke="#3b82f6" 
                  name="Confidence (%)"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Processing Time</CardTitle>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="avg_analysis_time" 
                  stroke="#8b5cf6" 
                  name="Analysis Time (ms)"
                />
                <Line 
                  type="monotone" 
                  dataKey="avg_parse_time" 
                  stroke="#ec4899" 
                  name="Parse Time (ms)"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Daily Usage</CardTitle>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="total_attempts" 
                  stroke="#f59e0b" 
                  name="Total Attempts"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
